import { Link } from 'react-router-dom';
import { GAME_MODES, modeLabel } from '@shared/chart';
import { listNcsSongs } from '@shared/seo/ncsSongCatalogue';
import { SITE_URL } from '@shared/seo/seoMeta';
import { modePath } from '../game/modeRouting';
import { TrademarkDisclaimer } from './TrademarkDisclaimer';
import './songsHub.css';

/**
 * Serialise a JSON-LD node for an inline `<script>`. Mirrors the server's
 * `seoTags` escaping (`<`, `>`, `&` → `\uXXXX`) so a song title can never
 * break out of the block. Only Google (which renders JS) sees this body-level
 * node; the server head already injects the page's WebApplication + breadcrumb
 * graph.
 */
function serialiseJsonLd(node: Record<string, unknown>): string {
  return JSON.stringify(node)
    .replace(/</g, '\\u003c')
    .replace(/>/g, '\\u003e')
    .replace(/&/g, '\\u0026');
}

/** Five-pip difficulty meter (1–5), reused across the hub. */
function DifficultyPips({ difficulty }: { difficulty: number }) {
  return (
    <span className="song-pips" aria-label={`Difficulty ${difficulty} of 5`}>
      {Array.from({ length: 5 }, (_, i) => (
        <span key={i} className={i < difficulty ? 'song-pip song-pip-on' : 'song-pip'} />
      ))}
    </span>
  );
}

/**
 * Tier-3 content hub index: "Songs to Play". Lists every track
 * in the catalogue with a route into its per-song page (where available game
 * modes become play CTAs). Top-of-funnel referral surface — genuine, sourced
 * song info (artist, tempo, difficulty), not a thin doorway.
 *
 * The head for `/songs` is resolved by `resolveClientRouteMeta` (RouteHead) on
 * SPA nav and injected server-side on first load, so this page renders no
 * `useDocumentHead` of its own; it adds only the accurate ItemList JSON-LD.
 */
export function SongsHubPage() {
  const songs = listNcsSongs();

  const itemListLd = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'Songs to Play',
    description:
      'Songs charted into free browser rhythm games at BeatHeaven.',
    numberOfItems: songs.length,
    itemListElement: songs.map((song, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: `${song.title} by ${song.artist}`,
      url: `${SITE_URL}/songs/${song.baseSlug}`,
    })),
  };

  return (
    <div className="songs-hub" data-testid="songs-hub">
      <header className="songs-hub-header">
        <p className="songs-hub-eyebrow">
          <Link to="/">BeatHeaven</Link> · Music
        </p>
        <h1>Songs to Play</h1>
        <p className="songs-hub-lede">
          Discover music from NCS and more in BeatHeaven’s six
          browser rhythm games inspired by osu, Piano Tiles, Guitar Hero / Clone
          Hero and Geometry Dash. Play any of them free — no download, and
          unblocked at school.
        </p>
        <nav className="songs-hub-modes" aria-label="Game modes">
          {GAME_MODES.map((mode) => (
            <Link key={mode} to={modePath(mode)} className="songs-hub-mode-chip">
              {modeLabel(mode)}
            </Link>
          ))}
        </nav>
      </header>

      <section className="songs-hub-section" aria-labelledby="songs-hub-tracks">
        <h2 id="songs-hub-tracks">{songs.length} tracks to play</h2>
        <ol className="songs-hub-list">
          {songs.map((song, i) => (
            <li key={song.baseSlug}>
              <Link
                to={`/songs/${song.baseSlug}`}
                className="song-card"
                data-testid={`song-card-${song.baseSlug}`}
              >
                <span className="song-card-rank">{String(i + 1).padStart(2, '0')}</span>
                <div className="song-card-body">
                  <h3 className="song-card-title">
                    {song.title}
                    <span className="song-card-artist"> · {song.artist}</span>
                  </h3>
                  <p className="song-card-desc">{song.description}</p>
                  <div className="song-card-meta">
                    <span className="song-card-bpm">{song.displayBpm} BPM</span>
                    <span>{song.label}</span>
                    <DifficultyPips difficulty={song.difficulty} />
                    <span>{song.playModes.map(({ mode }) => modeLabel(mode)).join(' · ')}</span>
                    <span className="song-card-cta">
                      Where to play <span aria-hidden="true">→</span>
                    </span>
                  </div>
                </div>
              </Link>
            </li>
          ))}
        </ol>
      </section>

      <section className="songs-hub-section songs-hub-about" aria-labelledby="songs-hub-about">
        <h2 id="songs-hub-about">Meet the music</h2>
        <p>
          Most tracks here are NCS (NoCopyrightSounds) releases; Once Again by
          Tristam is a Monstercat release. Each song page links to its official
          release and lists the game modes where you can play it.
        </p>
        <h2>Find your next rhythm game</h2>
        <p>
          Click osu-inspired circles, catch Piano Tiles-inspired tiles, shred a
          Guitar Hero / Clone Hero-inspired fretboard, or fly and jump through
          Geometry-Dash-inspired courses. Each song page lists the modes where
          that recording is available. You can also explore{' '}
          <Link to={modePath('pulse', 'browse')}>community maps</Link>.
        </p>
      </section>

      <TrademarkDisclaimer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: serialiseJsonLd(itemListLd) }}
      />
    </div>
  );
}
